import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AUTH } from "@/constants/testIds";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const r = await login(email.trim().toLowerCase(), password);
    setBusy(false);
    if (!r.ok) {
      toast.error(r.error || "Login failed");
      return;
    }
    toast.success("Welcome back");
    nav("/");
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2">
      {/* Hero — DVC-CIL industrial illustration */}
      <div className="relative hidden md:flex flex-col justify-between overflow-hidden border-r border-[#DCD9D0]" style={{ background: "linear-gradient(180deg, #EAF2FB 0%, #FFFFFF 60%)" }}>
        <div className="px-10 pt-10 flex items-center gap-3">
          <img src="/dvc-cil-logo.jpg" alt="DVC-CIL" className="h-14 w-14 object-contain shrink-0" />
          <div>
            <div className="font-heading text-[22px] font-bold text-[#1D3557] leading-tight">DVC-CIL Power Private Limited</div>
            <div className="font-mono-eo text-[10px] uppercase tracking-widest text-[#5C5C54] mt-1">Official e-Office Portal</div>
          </div>
        </div>
        <div className="px-10">
          <h1 className="font-heading font-bold text-[44px] leading-[1.05] text-[#0A0A0A] max-w-lg">
            Documents, moved with intent.
          </h1>
          <p className="text-[#5C5C54] mt-3 max-w-md text-[14px]">
            A modern, audit-first workspace for drafting, reviewing and approving official documents across DVC-CIL Power Private Limited.
          </p>
        </div>
        <img
          src="/login-hero.webp"
          alt="DVC-CIL Power plant illustration"
          className="w-full object-contain object-bottom"
          style={{ maxHeight: "38vh" }}
        />
      </div>
      {/* Form */}
      <div className="flex items-center justify-center px-8 py-12 bg-[#FBFAF3]">
        <div className="w-full max-w-sm">
          <div className="mb-8">
            <div className="font-mono-eo text-[10px] uppercase tracking-widest text-[#8A8A80] mb-2">// Portal Access</div>
            <h2 className="font-heading text-[36px] font-bold leading-tight">Sign in</h2>
            <p className="text-[14px] text-[#5C5C54] mt-2">Enter your credentials to access the DVC-CIL Power Private Limited e-Office portal.</p>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-[12px] font-mono-eo uppercase tracking-widest text-[#5C5C54] mb-1.5">Email</label>
              <input
                data-testid={AUTH.emailInput}
                type="email" required autoFocus
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="eo-input"
              />
            </div>
            <div>
              <label className="block text-[12px] font-mono-eo uppercase tracking-widest text-[#5C5C54] mb-1.5">Password</label>
              <div className="relative">
                <input
                  data-testid={AUTH.passwordInput}
                  type={showPw ? "text" : "password"} required
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  className="eo-input pr-10"
                />
                <button
                  type="button"
                  data-testid="login-password-toggle"
                  onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? "Hide password" : "Show password"}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-[#5C5C54] hover:text-[#0A0A0A]"
                  tabIndex={-1}
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <button data-testid={AUTH.submitBtn} disabled={busy} className="eo-btn-primary w-full mt-2">
              {busy ? "Signing in..." : "Sign in"}
            </button>
          </form>
          <div className="mt-6 pt-6 border-t border-[#DCD9D0]">
            <p className="text-[12px] text-[#8A8A80]">
              Have an invitation? <Link className="text-[#1D3557] underline" to="/accept-invite">Set your password</Link>
            </p>
            <p className="text-[12px] text-[#8A8A80] mt-2">
              Forgot your password? <Link data-testid="login-forgot-link" className="text-[#1D3557] underline" to="/forgot-password">Reset it</Link>
            </p>
            <p className="text-[11px] text-[#8A8A80] font-mono-eo mt-6 tracking-wide">
              © {new Date().getFullYear()} DVC-CIL Power Private Limited
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
